import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import TestimonialCarousel from "./TestimonialCarousel";
import { colors } from "../../Theme/theme";
import PhoneIcon from "@mui/icons-material/Phone";
import TranslateIcon from "@mui/icons-material/Translate";
import { FIRM } from "../../constants/firm";

const HeroWrapper = styled(Box)(({ theme }) => ({
	minHeight: "100vh",
	height: "100vh",
	display: "flex",
	flexDirection: "row",
	overflow: "hidden",
	position: "relative",
	backgroundColor: "#f5f6f8",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		height: "auto",
		minHeight: "100vh",
	},
}));

const LeftPanel = styled(Box)(({ theme }) => ({
	flex: "0 0 55%",
	minWidth: 0,
	boxSizing: "border-box",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	padding: theme.spacing(14, 8, 8, 10),
	[theme.breakpoints.down("lg")]: {
		padding: theme.spacing(12, 6, 6, 6),
	},
	[theme.breakpoints.down("md")]: {
		flex: "none",
		padding: theme.spacing(12, 4, 6, 4),
		alignItems: "center",
		textAlign: "center",
	},
}));

const RightPanel = styled(Box)(({ theme }) => ({
	flex: "0 0 45%",
	minWidth: 0,
	boxSizing: "border-box",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	position: "relative",
	overflow: "hidden",
	padding: theme.spacing(4, 6),
	background: `
		radial-gradient(ellipse at 110% -10%, rgba(162, 192, 255, 0.18) 0%, transparent 55%),
		linear-gradient(165deg, ${colors.primary.dark} 0%, ${colors.primary.darker} 55%, #071a3e 100%)
	`,
	[theme.breakpoints.down("lg")]: {
		padding: theme.spacing(4, 4),
	},
	[theme.breakpoints.down("md")]: {
		flex: "none",
		width: "100%",
		minHeight: "55vh",
		padding: theme.spacing(4, 3),
	},
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
	fontSize: "0.78rem",
	fontWeight: 700,
	letterSpacing: "0.18em",
	color: colors.primary.main,
	textTransform: "uppercase",
	marginBottom: theme.spacing(2.5),
}));

const FirmName = styled(Typography)(({ theme }) => ({
	fontFamily: "Georgia, 'Times New Roman', serif",
	fontSize: "clamp(3.2rem, 5vw, 5.5rem)",
	fontWeight: 700,
	color: colors.primary.darker,
	lineHeight: 1.05,
	letterSpacing: "-0.02em",
	marginBottom: theme.spacing(2.5),
}));

const GoldDivider = styled(Box)(() => ({
	width: "64px",
	height: "4px",
	background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
	marginBottom: "24px",
}));

const Tagline = styled(Typography)(({ theme }) => ({
	fontFamily: "Georgia, 'Times New Roman', serif",
	fontSize: "clamp(1.6rem, 2.5vw, 2.6rem)",
	fontStyle: "italic",
	fontWeight: 400,
	color: colors.primary.main,
	marginBottom: theme.spacing(3),
}));

const Descriptor = styled(Typography)(({ theme }) => ({
	fontSize: "1.1rem",
	lineHeight: 1.75,
	color: "#4a5568",
	maxWidth: "460px",
	marginBottom: theme.spacing(4),
	fontFamily: "'Heebo', sans-serif",
	[theme.breakpoints.down("md")]: {
		maxWidth: "100%",
	},
}));

const CTARow = styled(Box)(({ theme }) => ({
	display: "flex",
	gap: theme.spacing(2),
	alignItems: "center",
	flexWrap: "wrap",
	marginBottom: theme.spacing(3),
	[theme.breakpoints.down("md")]: {
		justifyContent: "center",
	},
}));

const fadeUp = (delay = 0) => ({
	initial: { opacity: 0, y: 24 },
	animate: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] },
	},
});

const Hero = () => {
	return (
		<HeroWrapper>
			<LeftPanel>
				<motion.div {...fadeUp(0)}>
					<Eyebrow>Personal Injury Law · Albuquerque, New Mexico</Eyebrow>
				</motion.div>

				<motion.div {...fadeUp(0.15)}>
					<FirmName component="h1">
						David Rhee
						<br />
						Injury Law
					</FirmName>
				</motion.div>

				<motion.div
					initial={{ opacity: 0, scaleX: 0 }}
					animate={{
						opacity: 1,
						scaleX: 1,
						transition: { duration: 0.55, delay: 0.32, ease: "easeOut" },
					}}
					style={{ transformOrigin: "left" }}
				>
					<GoldDivider />
				</motion.div>

				<motion.div {...fadeUp(0.42)}>
					<Tagline>Just Results.</Tagline>
				</motion.div>

				<motion.div {...fadeUp(0.55)}>
					<Descriptor>
						When an injury disrupts your life, you need attorneys who fight — not fold. We represent
						accident victims across New Mexico with relentless advocacy and a free consultation to get you
						started.
					</Descriptor>
				</motion.div>

				<motion.div {...fadeUp(0.68)}>
					<CTARow>
						<Button
							variant="contained"
							size="large"
							href="#Contact"
							sx={{
								backgroundColor: colors.primary.main,
								color: "#fff",
								px: 4,
								py: 1.5,
								fontSize: "0.92rem",
								fontWeight: 700,
								borderRadius: 0,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								boxShadow: "0 4px 16px rgba(26, 71, 137, 0.3)",
								"&:hover": {
									backgroundColor: colors.primary.dark,
									boxShadow: "0 6px 20px rgba(26, 71, 137, 0.4)",
									transform: "translateY(-1px)",
								},
								transition: "all 0.2s ease",
							}}
						>
							Free Consultation
						</Button>
						<Button
							variant="outlined"
							size="large"
							href={FIRM.phone.href}
							startIcon={<PhoneIcon sx={{ fontSize: "1rem !important" }} />}
							sx={{
								borderColor: colors.primary.main,
								borderWidth: "1.5px",
								color: colors.primary.main,
								px: 3,
								py: 1.5,
								fontSize: "0.92rem",
								borderRadius: 0,
								fontWeight: 600,
								"&:hover": {
									backgroundColor: "rgba(26, 71, 137, 0.05)",
									borderColor: colors.primary.dark,
									borderWidth: "1.5px",
									transform: "translateY(-1px)",
								},
								transition: "all 0.2s ease",
							}}
						>
							{FIRM.phone.display}
						</Button>
					</CTARow>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 1.5,
							mt: 0.5,
							px: 2,
							py: 1.25,
							backgroundColor: "rgba(250, 212, 97, 0.14)",
							border: `1px solid ${colors.accent.dark}`,
							borderLeft: `4px solid ${colors.accent.main}`,
						}}
					>
						<TranslateIcon sx={{ fontSize: "1.2rem", color: colors.accent.dark, flexShrink: 0 }} />
						<Box>
							<Typography
								sx={{
									fontSize: "0.95rem",
									fontWeight: 700,
									color: colors.primary.darker,
									lineHeight: 1.25,
									fontFamily: "'Heebo', sans-serif",
									letterSpacing: "0.02em",
								}}
							>
								Hablamos Español
							</Typography>
							<Typography
								sx={{
									fontSize: "0.7rem",
									color: "#4a5568",
									letterSpacing: "0.06em",
									textTransform: "uppercase",
									fontFamily: "'Heebo', sans-serif",
								}}
							>
								servicio en español disponible
							</Typography>
						</Box>
					</Box>
				</motion.div>
			</LeftPanel>

			<RightPanel>
				<Box
					sx={{
						position: "absolute",
						top: "15%",
						left: 0,
						width: "3px",
						height: "70%",
						background: `linear-gradient(to bottom, transparent, ${colors.accent.main}, transparent)`,
						opacity: 0.4,
						pointerEvents: "none",
					}}
				/>
				<TestimonialCarousel />
			</RightPanel>

			{/* Split bottom bar: left matches Services rail, right matches RightPanel bottom */}
			<Box
				sx={{
					position: "absolute",
					bottom: 0,
					left: 0,
					right: 0,
					height: "16px",
					display: { xs: "none", md: "block" },
					zIndex: 10,
					pointerEvents: "none",
					background: `linear-gradient(90deg, ${colors.primary.main} 40%, ${colors.primary.darker} 52%, #071a3e 65%)`,
				}}
			/>
		</HeroWrapper>
	);
};

export default Hero;
